import type { PickemQuestion } from '@low6-pickem/api-types';

type OverUnderAnswer = 'over' | 'under';
type PickemOption = PickemQuestion['options'][number];

const comparisonOf = (option: PickemOption): OverUnderAnswer | undefined => {
  const comparison = (option.grading_data as { comparison?: unknown } | null | undefined)
    ?.comparison;
  return comparison === 'over' || comparison === 'under' ? comparison : undefined;
};

/**
 * The option that stands for an over/under answer.
 *
 * The option's own `grading_data.comparison` decides first. A yes/no market ("Will Larson finish
 * in the top 10?") is stored as an over/under whose options are titled Yes and No, so its title
 * says nothing about which side it is, and Yes can be the UNDER. Options arrive in random (UUID)
 * order, so falling straight through to position would submit the other answer about half the
 * time. Title and position remain the fallback for older options with no comparison.
 */
export function findOverUnderOption(
  question: PickemQuestion,
  answer: OverUnderAnswer
): PickemOption | undefined {
  return (
    question.options.find(option => comparisonOf(option) === answer) ??
    question.options.find(option => option.title_en?.toLowerCase().includes(answer)) ??
    question.options[answer === 'over' ? 0 : 1]
  );
}

/** The inverse of `findOverUnderOption`: which answer a stored option is. */
export function overUnderAnswerOf(question: PickemQuestion, option: PickemOption): OverUnderAnswer {
  const comparison = comparisonOf(option);
  if (comparison) return comparison;
  const title = option.title_en?.toLowerCase();
  if (title?.includes('over')) return 'over';
  if (title?.includes('under')) return 'under';
  return question.options.indexOf(option) === 0 ? 'over' : 'under';
}
